import { getTranslationGroupLabel } from '../../app/terminology/components';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import agent from "../../app/api/agent";
import { fieldChecked, fieldValue, useModalOpen } from "../../app/components/polaris";
import type { StorefrontLanguage, StorefrontTranslationEntry, StorefrontTranslations } from "../../app/models/StorefrontTranslations";

export type StorefrontTranslationsEditorStatus = {
    text?: Record<string, string>;
    locale?: string;
    error?: string;
    reload?: () => void;
    hasChanges: boolean;
    loading: boolean;
    saving: boolean;
    save?: () => Promise<void>;
    discard?: () => void;
};

type StorefrontTranslationsSettingsProps = {
    navigationTarget?: { group: string; requestId: number; query?: string };
    onStatusChange?: (status: StorefrontTranslationsEditorStatus) => void;
    onHeaderActionsChange?: (content: ReactNode | null) => void;
};

const snapshot = (values: Record<string, string>) => JSON.stringify(
    Object.keys(values)
        .sort((left, right) => left.localeCompare(right))
        .reduce<Record<string, string>>((accumulator, key) => {
            accumulator[key] = values[key] || "";
            return accumulator;
        }, {})
);

const entryValues = (entries: StorefrontTranslationEntry[]) =>
    entries.reduce<Record<string, string>>((accumulator, entry) => {
        accumulator[entry.key] = entry.value || "";
        return accumulator;
    }, {});

const languageLabel = (language: StorefrontLanguage) => {
    const parts = [language.name || language.locale.toUpperCase()];
    if (language.primary) parts.push("Default");
    if (!language.published) parts.push("Unpublished");
    return `${parts.join(" - ")} (${language.locale})`;
};

const groupEntries = (entries: StorefrontTranslationEntry[]) =>
    entries.reduce<Record<string, StorefrontTranslationEntry[]>>((groups, entry) => {
        groups[entry.group] = groups[entry.group] || [];
        groups[entry.group].push(entry);
        return groups;
    }, {});

const groupOrder = [
    "Vehicle Search",
    "Selector fields",
    "Product Results",
    "Fit Check",
    "Fitment Table",
    "Product Guide",
];

const selectorFieldValueOrder: Record<string, number> = {
    display_label: 1,
    placeholder: 2,
    empty_option: 3,
};

const selectorFieldFallbackId = (entry: StorefrontTranslationEntry) =>
    entry.entityId || entry.entityKey || entry.entityLabel || entry.key;

const sortTranslationGroups = (groups: [string, StorefrontTranslationEntry[]][]) =>
    groups.sort(([left], [right]) => {
        const leftIndex = groupOrder.indexOf(left);
        const rightIndex = groupOrder.indexOf(right);
        if (leftIndex !== -1 || rightIndex !== -1) {
            return (leftIndex === -1 ? Number.MAX_SAFE_INTEGER : leftIndex)
                - (rightIndex === -1 ? Number.MAX_SAFE_INTEGER : rightIndex);
        }

        return left.localeCompare(right);
    });

const groupSelectorFieldEntries = (entries: StorefrontTranslationEntry[]) => {
    const groups = entries.reduce<Record<string, StorefrontTranslationEntry[]>>((accumulator, entry) => {
        const id = selectorFieldFallbackId(entry);
        accumulator[id] = accumulator[id] || [];
        accumulator[id].push(entry);
        return accumulator;
    }, {});

    return Object.entries(groups)
        .map(([id, fieldEntries]) => ({
            id,
            label: fieldEntries[0]?.entityLabel || fieldEntries[0]?.entityKey || "Selector field",
            displayOrder: fieldEntries[0]?.displayOrder ?? Number.MAX_SAFE_INTEGER,
            entries: fieldEntries.slice().sort((left, right) =>
                (selectorFieldValueOrder[left.valueType || ""] ?? 99)
                - (selectorFieldValueOrder[right.valueType || ""] ?? 99)
            ),
        }))
        .sort((left, right) => (left.displayOrder - right.displayOrder) || left.label.localeCompare(right.label));
};

// Storefront copy editor. Blank fields intentionally mean "use the built-in pack or English default".
const StorefrontTranslationsSettings = ({ onStatusChange, onHeaderActionsChange, navigationTarget }: StorefrontTranslationsSettingsProps) => {
    const [data, setData] = useState<StorefrontTranslations>();
    const [previewData, setPreviewData] = useState<StorefrontTranslations>();
    const [selectedLocale, setSelectedLocale] = useState("");
    const [selectedPreset, setSelectedPreset] = useState("");
    const [overwritePresetText, setOverwritePresetText] = useState(false);
    const [presetDialogOpen, setPresetDialogOpen] = useState(false);
    const [presetError, setPresetError] = useState<string>();
    const [draft, setDraft] = useState<Record<string, string>>({});
    const [baseline, setBaseline] = useState<Record<string, string>>({});
    const [selectedGroupTab, setSelectedGroupTab] = useState(0);
    const [searchQuery, setSearchQuery] = useState("");
    const [focusedEntryKey, setFocusedEntryKey] = useState<string>();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [applyingPreset, setApplyingPreset] = useState(false);
    const [previewingPreset, setPreviewingPreset] = useState(false);
    const [error, setError] = useState<string>();
    const presetPreviewRequestId = useRef(0);

    const loadTranslations = useCallback(async (locale?: string) => {
        // The backend returns Shopify languages plus all editable label definitions for this locale.
        presetPreviewRequestId.current += 1;
        setLoading(true);
        setPreviewingPreset(false);
        setPreviewData(undefined);
        setError(undefined);

        try {
            const response = await agent.Settings.storefrontTranslations(locale);
            const values = entryValues(response.entries);
            setData(response);
            setPreviewData(undefined);
            setSelectedLocale(response.selectedLocale);
            setSelectedPreset(response.selectedTerminologyPreset);
            setOverwritePresetText(false);
            setDraft(values);
            setBaseline(values);
        } catch (err: any) {
            setError(err?.message ?? "Failed to load translations.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadTranslations();
    }, [loadTranslations]);

    const hasChanges = snapshot(draft) !== snapshot(baseline);
    const presetHasChanges = !!data && selectedPreset !== data.selectedTerminologyPreset;
    const displayEntries = useMemo(
        () => previewData?.entries ?? data?.entries ?? [],
        [data?.entries, previewData?.entries]
    );
    const groupedEntries = useMemo(() => groupEntries(displayEntries), [displayEntries]);
    const groups = useMemo(() => sortTranslationGroups(Object.entries(groupedEntries)), [groupedEntries]);
    const handledNavigation = useRef<number | undefined>(undefined);
    useEffect(() => {
        if (!navigationTarget || !groups.length || handledNavigation.current === navigationTarget.requestId) return;
        const index = groups.findIndex(([group]) => group === navigationTarget.group);
        if (index < 0) return;
        handledNavigation.current = navigationTarget.requestId;
        setSelectedGroupTab(index);
        setSearchQuery(navigationTarget.query || '');
        setFocusedEntryKey(undefined);
    }, [navigationTarget, groups]);

    const languageOptions = useMemo(
        () => (data?.languages ?? []).map((language) => ({
            label: languageLabel(language),
            value: language.locale,
        })),
        [data?.languages]
    );
    const presetOptions = useMemo(
        () => (data?.terminologyPresets ?? []).map((preset) => ({
            label: preset.label,
            value: preset.value,
        })),
        [data?.terminologyPresets]
    );

    const selectedLanguage = useMemo(
        () => (data?.languages ?? []).find((language) => language.locale === selectedLocale),
        [data?.languages, selectedLocale]
    );

    const appliedPresetLabel = presetOptions.find(option => option.value === data?.selectedTerminologyPreset)?.label;
    const openPresetDialog = useCallback(() => {
        setSelectedPreset(data?.selectedTerminologyPreset ?? "");
        setOverwritePresetText(false);
        setPresetError(undefined);
        setPresetDialogOpen(true);
    }, [data?.selectedTerminologyPreset]);
    const closePresetDialog = useCallback(() => {
        if (applyingPreset) return;
        presetPreviewRequestId.current += 1;
        setPreviewData(undefined);
        setPreviewingPreset(false);
        setSelectedPreset(data?.selectedTerminologyPreset ?? "");
        setOverwritePresetText(false);
        setPresetError(undefined);
        setPresetDialogOpen(false);
    }, [applyingPreset, data?.selectedTerminologyPreset]);

    useEffect(() => {
        if (!presetDialogOpen || !data || !selectedLocale || !selectedPreset) {
            presetPreviewRequestId.current += 1;
            setPreviewData(undefined);
            setPreviewingPreset(false);
            return;
        }

        if (selectedPreset === data.selectedTerminologyPreset && !overwritePresetText) {
            presetPreviewRequestId.current += 1;
            setPreviewData(undefined);
            setPreviewingPreset(false);
            return;
        }

        const requestId = presetPreviewRequestId.current + 1;
        presetPreviewRequestId.current = requestId;
        setPreviewingPreset(true);
        setPresetError(undefined);

        agent.Settings.previewStorefrontTerminologyPreset({
            locale: selectedLocale,
            preset: selectedPreset,
            overwriteCustomizedText: overwritePresetText,
        })
            .then((response) => {
                if (presetPreviewRequestId.current !== requestId) return;
                setPreviewData(response);
                setPresetError(undefined);
            })
            .catch((err: any) => {
                if (presetPreviewRequestId.current !== requestId) return;
                setPreviewData(undefined);
                setPresetError(err?.message ?? "Failed to preview terminology preset.");
            })
            .finally(() => {
                if (presetPreviewRequestId.current === requestId) {
                    setPreviewingPreset(false);
                }
            });
    }, [data, overwritePresetText, presetDialogOpen, selectedLocale, selectedPreset]);

    const handleLocaleChange = useCallback((locale: string) => {
        setSelectedLocale(locale);
        loadTranslations(locale);
    }, [loadTranslations]);

    useEffect(() => {
        setSelectedGroupTab((current) => Math.min(current, Math.max(groups.length - 1, 0)));
    }, [groups.length]);

    const updateDraftValue = useCallback((key: string, value: string) => {
        setDraft((current) => ({ ...current, [key]: value }));
    }, []);

    const displayValueForEntry = useCallback((entry: StorefrontTranslationEntry) => {
        if (previewData && !hasChanges) {
            return entry.value || "";
        }

        return draft[entry.key] ?? "";
    }, [draft, hasChanges, previewData]);

    const normalizedQuery = searchQuery.trim().toLocaleLowerCase();
    const matchingEntries = displayEntries.filter((entry) => {
        const value = displayValueForEntry(entry);
        return [entry.label, entry.entityLabel, value.trim() || entry.builtInValue || entry.defaultValue]
            .some((text) => text?.toLocaleLowerCase().includes(normalizedQuery));
    });
    // Keep a matching field mounted while its text is being edited, even if the new value no longer matches.
    const visibleGroups = normalizedQuery
        ? sortTranslationGroups(Object.entries(groupEntries(displayEntries.filter((entry) =>
            entry.key === focusedEntryKey || matchingEntries.includes(entry)
        ))))
        : groups.slice(selectedGroupTab, selectedGroupTab + 1);

    const renderEntry = (entry: StorefrontTranslationEntry) => {
        const props = {
            label: entry.label,
            value: displayValueForEntry(entry),
            placeholder: entry.builtInValue || entry.defaultValue || "Blank",
            disabled: loading || saving || applyingPreset,
            onInput: (event: Event) => updateDraftValue(entry.key, fieldValue(event)),
            onFocus: () => setFocusedEntryKey(entry.key),
            onBlur: () => setFocusedEntryKey(undefined),
        };
        return entry.multiline ? <s-text-area key={entry.key} {...props} rows={3} /> : <s-text-field key={entry.key} {...props} autocomplete="off" />;
    };

    const discardChanges = useCallback(() => {
        setDraft(baseline);
    }, [baseline]);

    const saveTranslations = useCallback(async () => {
        if (!selectedLocale) return;

        // Blank values are sent too; the API interprets them as delete override.
        presetPreviewRequestId.current += 1;
        setSaving(true);
        setPreviewingPreset(false);
        setPreviewData(undefined);
        setError(undefined);

        try {
            const response = await agent.Settings.updateStorefrontTranslations(selectedLocale, {
                translations: draft,
            });
            const values = entryValues(response.entries);
            setData(response);
            setPreviewData(undefined);
            setSelectedPreset(response.selectedTerminologyPreset);
            setDraft(values);
            setBaseline(values);
            window.shopify?.toast?.show?.("Storefront text saved");
        } catch (err: any) {
            setError(err?.message ?? "Failed to save translations.");
            throw err;
        } finally {
            setSaving(false);
        }
    }, [draft, selectedLocale]);

    const applyTerminologyPreset = useCallback(async () => {
        if (!selectedLocale || !selectedPreset || hasChanges) return;

        presetPreviewRequestId.current += 1;
        setApplyingPreset(true);
        setPreviewingPreset(false);
        setPreviewData(undefined);
        setError(undefined);
        setPresetError(undefined);

        try {
            const response = await agent.Settings.updateStorefrontTerminologyPreset({
                locale: selectedLocale,
                preset: selectedPreset,
                overwriteCustomizedText: overwritePresetText,
            });
            const values = entryValues(response.entries);
            setData(response);
            setPreviewData(undefined);
            setSelectedPreset(response.selectedTerminologyPreset);
            setOverwritePresetText(false);
            setDraft(values);
            setBaseline(values);
            window.shopify?.toast?.show?.("Terminology preset applied");
            setPresetDialogOpen(false);
        } catch (err: any) {
            setPresetError(err?.message ?? "Failed to apply terminology preset.");
        } finally {
            setApplyingPreset(false);
        }
    }, [hasChanges, overwritePresetText, selectedLocale, selectedPreset]);

    useEffect(() => {
        onStatusChange?.({
            text: data ? Object.fromEntries(data.entries.flatMap(entry => {
                const value = draft[entry.key]?.trim();
                // Untranslated selector defaults follow the field draft, including renames.
                if (entry.entityType === "SelectorField" && !value && entry.builtInValue === entry.defaultValue) return [];
                return [[entry.key, value || entry.builtInValue || entry.defaultValue]];
            })) : undefined,
            locale: data?.selectedLocale,
            error,
            reload: !data ? () => { void loadTranslations(); } : undefined,
            hasChanges,
            loading,
            saving: saving || applyingPreset,
            save: saveTranslations,
            discard: discardChanges,
        });
    }, [data, draft, error, loadTranslations, applyingPreset, discardChanges, hasChanges, loading, onStatusChange, saveTranslations, saving]);

    const busy = loading || saving || applyingPreset;
    const headerControls = useMemo(() => data ? (
        <s-button slot="secondary-actions" onClick={openPresetDialog} disabled={busy}>
            {appliedPresetLabel ? `Terminology: ${appliedPresetLabel}` : "Terminology preset"}
        </s-button>
    ) : null, [appliedPresetLabel, busy, data, openPresetDialog]);
    const canApplyPreset = !!selectedLocale
        && !!selectedPreset
        && !hasChanges
        && !loading
        && !saving
        && !applyingPreset
        && !previewingPreset
        && (presetHasChanges || overwritePresetText);

    useEffect(() => {
        if (!onHeaderActionsChange) return;
        onHeaderActionsChange(headerControls);
        return () => onHeaderActionsChange(null);
    }, [headerControls, onHeaderActionsChange]);

    const presetModalRef = useModalOpen(presetDialogOpen, closePresetDialog);

    if (loading && !data) {
        return (
            <s-section>
                <s-stack alignItems="center" padding="base">
                    <s-spinner accessibilityLabel="Loading storefront text" />
                </s-stack>
            </s-section>
        );
    }

    return (
        <>
            {data?.warning && <s-banner tone="warning">{data.warning}</s-banner>}
            {error && <s-banner tone="critical">{error}</s-banner>}
            {!onHeaderActionsChange && headerControls}

            <s-section>
                <s-stack gap="base">
                    <s-grid gridTemplateColumns="1fr 1fr" gap="base">
                        <s-select label="Language" value={selectedLocale} disabled={busy}
                            onChange={event => handleLocaleChange(fieldValue(event))}>
                            {languageOptions.map(option => <s-option key={option.value} value={option.value}>{option.label}</s-option>)}
                        </s-select>
                        <s-select label="Section" value={String(selectedGroupTab)} disabled={!!normalizedQuery || !groups.length}
                            onChange={event => setSelectedGroupTab(Number(fieldValue(event)))}>
                            {groups.map(([group], index) => <s-option key={group} value={String(index)}>{getTranslationGroupLabel(group)}</s-option>)}
                        </s-select>
                    </s-grid>
                    <s-search-field label="Search storefront text" labelAccessibilityVisibility="exclusive" placeholder="Search storefront text"
                        value={searchQuery} onInput={event => { setFocusedEntryKey(undefined); setSearchQuery(fieldValue(event)); }} />
                    {normalizedQuery && <s-text color="subdued">
                        {matchingEntries.length} {matchingEntries.length === 1 ? "result" : "results"}
                    </s-text>}
                </s-stack>
            </s-section>

            {visibleGroups.map(([group, entries]) => (
                <s-section key={group} heading={getTranslationGroupLabel(group)}>
                    <s-stack gap="base">
                        {group === "Selector fields"
                            ? groupSelectorFieldEntries(entries).map((fieldGroup, index) => (
                                <s-stack gap="base" key={fieldGroup.id}>
                                    {index > 0 && <s-divider />}
                                    <s-heading>{fieldGroup.label}</s-heading>
                                    {fieldGroup.entries.map(renderEntry)}
                                </s-stack>
                            ))
                            : entries.map(renderEntry)}
                    </s-stack>
                </s-section>
            ))}
            {visibleGroups.length === 0 && <s-section>
                <s-text color="subdued">{normalizedQuery ? "No matching text" : "No storefront text found"}</s-text>
            </s-section>}

            <s-modal ref={presetModalRef} id="pf-terminology-preset" heading="Terminology preset">
                {/* s-modal keeps its children mounted; render the form only while open. */}
                {data && presetDialogOpen && <s-stack gap="base">
                    {presetError && <s-banner tone="critical">{presetError}</s-banner>}
                    <s-select label="Terminology preset" value={selectedPreset} disabled={busy}
                        onChange={event => setSelectedPreset(fieldValue(event))}>
                        {presetOptions.map(option => <s-option key={option.value} value={option.value}>{option.label}</s-option>)}
                    </s-select>
                    <s-checkbox label={`Replace customized text for ${selectedLanguage?.name || selectedLocale}`} checked={overwritePresetText}
                        disabled={busy} onChange={event => setOverwritePresetText(fieldChecked(event))} />
                    {hasChanges && <s-text color="subdued">Save or discard text changes before applying a preset.</s-text>}
                </s-stack>}
                <s-button slot="primary-action" variant="primary" onClick={applyTerminologyPreset} loading={applyingPreset}
                    disabled={!canApplyPreset}>Apply preset</s-button>
                <s-button slot="secondary-actions" onClick={closePresetDialog} disabled={applyingPreset}>Cancel</s-button>
            </s-modal>
        </>
    );
};

export default StorefrontTranslationsSettings;
